// ============================================================================
// src/lib/discount-display.ts
// PV-1052 · M4 — ADR-055 Lot 5 "scope-exact discount display" (supersedes the
// ADR-047 gating).
//
// Single source of truth for how a deal's discount is surfaced on the card /
// sidebar / Home / gallery / dashboards. ADR-055 INVERTS the ADR-047 rule:
// EVERY positive discount is shown publicly, whatever the scope or confidence
// — but always labelled with its EXACT perimeter (building / project group /
// community / zone) so a zone-level discount is never presented as a
// same-building comparison. Confidence becomes a transparency label, no
// longer a display gate.
//
// The cohort/matching logic is unchanged — this only classifies the DISPLAY.
// Negative discounts are kept signed in the engine and the admin BO; the
// public surfaces simply show no badge.
// ============================================================================

import { formatDiscountPct } from './format';

/** Scope-aware confidence persisted on deals.dld_confidence (ADR-055). */
export type DldConfidence = 'high' | 'medium' | 'low' | 'insufficient' | 'off_plan_reference';

/**
 * The EXACT perimeter family a positive discount is benchmarked against:
 *  - `building`      : same_building_* — the deal's own canonical building.
 *  - `project_group` : same_project_group_* — the towers of the same project.
 *  - `community`     : same_community_* and broader_market (community ±1 BR).
 *  - `zone`          : same_zone_* and zone_broader (zone ±1 BR).
 *  - `developer`     : off-plan reference price (not DLD).
 *  - `suppressed`    : nothing to surface (no positive saving, insufficient
 *                      data, unknown/legacy scope, or confidence not computed).
 */
export type DiscountDisplayMode =
  | 'building'
  | 'project_group'
  | 'community'
  | 'zone'
  | 'developer'
  | 'suppressed';

/**
 * Resolve the display mode from the persisted confidence + scope + pct.
 *
 * `pct` is the persisted (positive = below reference) discount snapshot. A
 * non-positive pct always suppresses the badge — there is no saving to
 * surface (the signed value stays available to the engine and the admin BO).
 * An unknown scope (e.g. a legacy persisted value awaiting the ADR-055
 * backfill) is suppressed rather than mislabelled.
 */
export function discountDisplayMode(
  confidence: string | null | undefined,
  scope: string | null | undefined,
  pct: number | null | undefined,
): DiscountDisplayMode {
  const value = typeof pct === 'number' ? pct : 0;

  // Off-plan: the reference is the original purchase price, not DLD. Surface
  // the saving with explicit "vs Original price" wording. (The mode is named
  // `developer` for historical reasons; the user-facing label is "Original
  // price" — see `usesOriginalPriceReference` / dealCard.originalPrice.)
  if (confidence === 'off_plan_reference') {
    return value > 0 ? 'developer' : 'suppressed';
  }

  // No saving, insufficient data, or confidence not yet computed → nothing.
  if (value <= 0 || confidence === 'insufficient' || confidence == null) {
    return 'suppressed';
  }

  if (typeof scope !== 'string') return 'suppressed';

  if (scope.startsWith('same_building_')) return 'building';
  if (scope.startsWith('same_project_group_')) return 'project_group';
  if (scope.startsWith('same_community_') || scope === 'broader_market') return 'community';
  if (scope.startsWith('same_zone_') || scope === 'zone_broader') return 'zone';

  return 'suppressed';
}

/**
 * Whether a deal's headline price reference is its ORIGINAL purchase price
 * (off-plan) rather than the DLD transaction benchmark (ready). Single source
 * of truth for the reference-label wording on the card + Home hero. Driven by
 * the deal TYPE, never by the presence of a comparison value — an off-plan
 * deal with no positive discount, or a sold off-plan deal, still reads
 * "Original price".
 */
export function usesOriginalPriceReference(
  dealType: 'ready' | 'off_plan' | null | undefined,
): boolean {
  return dealType === 'off_plan';
}

/**
 * Compact discount text for a dense table cell (buyer dashboards): the signed
 * percentage whenever a perimeter-labelled positive discount exists (ADR-055 —
 * every positive discount is public), otherwise a neutral em-dash.
 */
export function discountCellText(
  confidence: string | null | undefined,
  scope: string | null | undefined,
  pct: number | null | undefined,
): string {
  const mode = discountDisplayMode(confidence, scope, pct);
  if (mode === 'suppressed') return '—';
  return formatDiscountPct(pct ?? 0);
}

/**
 * i18n key for the deal-card discount badge suffix, by display mode. Off-plan
 * (developer reference) reads "below original price"; every DLD-based perimeter
 * reads the commercial "below market". `suppressed` → null (no badge rendered).
 * The exact perimeter (building vs community/zone) is disclosed on the deal
 * page (BenchmarkPanel + sidebar tags), not on the compact card.
 */
export function discountBadgeLabelKey(mode: DiscountDisplayMode): string | null {
  if (mode === 'suppressed') return null;
  if (mode === 'developer') return 'common:dealCard.discountBadge.belowOriginalPrice';
  return 'common:dealCard.discountBadge.belowMarket';
}
